/**
 * TelemetryTable — the line-by-line log view.
 *
 * Virtualised: a thirty-minute flight is eighteen thousand rows and putting
 * that in the DOM makes scrubbing stutter. Only the visible window is rendered,
 * with spacer rows holding the scroll height.
 *
 * Rows are clickable and the table follows the playhead, so the map, the
 * profile and the table are always describing the same instant.
 */

import { fmt, units } from './contract.js';

const ROW_HEIGHT = 21;
const OVERSCAN = 12;

/**
 * Rows the rendered window snaps to.
 *
 * Large enough that smooth scrolling crosses a boundary rarely, small enough
 * that the rebuilt block stays cheap. At a row height of 21 px this is about
 * two viewports of movement between rebuilds.
 */
const BLOCK = 24;

export class TelemetryTable {
  constructor(container, flight, onSelect) {
    this.container = container;
    this.flight = flight;
    this.onSelect = onSelect;
    this.current = 0;
    this._following = true;
    this._pending = false;
    this._lastAutoScroll = 0;
    this._renderedFirst = -1;
    this._renderedLast = -1;
    this._currentRow = null;

    this.columns = this.buildColumns();
    this.render();
    this.bind();
  }

  /**
   * Only offer columns the log actually populated. A table of em dashes
   * teaches the reader nothing and pushes the useful columns off screen.
   *
   * Unit-bearing columns carry the current system in their header and convert
   * their values, so the header toggle reaches the table too.
   */
  buildColumns() {
    const f = this.flight;
    const alt = units.label('altitude');
    const spd = units.label('speed');
    const dst = units.label('distance');

    // Widths are declared, not measured.
    //
    // Left to itself the browser sizes columns from whatever rows happen to be
    // in the DOM, and a virtualised table swaps those out on every scroll. A
    // window containing a long message produces one set of widths and the next
    // window produces another, so the whole table shifts sideways as you
    // scroll. Fixed layout plus an explicit width per column stops it dead.
    const all = [
      { key: 'timeS', label: 'Time', icon: 'fa-clock', align: 'left', width: 66, get: (r) => fmt.elapsed(r.timeS) },
      { key: 'flightModeCode', label: 'Mode', icon: 'fa-gauge-simple-high', align: 'left', width: 116, get: (r) => r.flightMode || '—' },
      { key: 'satellites', label: 'GPS', icon: 'fa-satellite-dish', width: 54, get: (r) => num(r.satellites, 0) },
      { key: 'uplinkSignalPct', label: 'Uplink', icon: 'fa-tower-broadcast', width: 72, get: (r) => num(r.uplinkSignalPct, 0) },
      { key: 'downlinkSignalPct', label: 'Downlink', icon: 'fa-tower-broadcast', width: 82, get: (r) => num(r.downlinkSignalPct, 0) },
      { key: 'heightM', label: `Height ${alt}`, icon: 'fa-arrows-up-down', width: 88, get: (r) => conv(r.heightM, 'altitude', 1) },
      { key: 'vpsAltitudeM', label: `VPS ${alt}`, icon: 'fa-ruler-vertical', width: 78, get: (r) => conv(r.vpsAltitudeM, 'altitude', 1) },
      { key: 'speedHorizontalMs', label: `Speed ${spd}`, icon: 'fa-gauge-high', width: 92, get: (r) => conv(r.speedHorizontalMs, 'speed', 1) },
      { key: 'homeDistanceM', label: `Home ${dst}`, icon: 'fa-house', width: 84, get: (r) => conv(r.homeDistanceM, 'distance', 0) },
      { key: 'batteryPercent', label: 'Batt %', icon: 'fa-battery-three-quarters', width: 66, get: (r) => num(r.batteryPercent, 0) },
      { key: 'battery2Percent', label: 'Batt 2 %', icon: 'fa-battery-half', width: 74, get: (r) => num(r.battery2Percent, 0) },
      { key: 'batteryVoltageV', label: 'Volts', icon: 'fa-bolt', width: 68, get: (r) => num(r.batteryVoltageV, 2) },
      { key: 'batteryCurrentA', label: 'Amps', icon: 'fa-gauge', width: 66, get: (r) => num(r.batteryCurrentA, 1) },
      { key: 'batteryCell1V', label: 'Cell 1', icon: 'fa-car-battery', width: 66, get: (r) => num(r.batteryCell1V, 3) },
      { key: 'batteryCell2V', label: 'Cell 2', icon: 'fa-car-battery', width: 66, get: (r) => num(r.batteryCell2V, 3) },
      { key: 'batteryCell3V', label: 'Cell 3', icon: 'fa-car-battery', width: 66, get: (r) => num(r.batteryCell3V, 3) },
      { key: 'batteryCell4V', label: 'Cell 4', icon: 'fa-car-battery', width: 66, get: (r) => num(r.batteryCell4V, 3) },
      { key: 'batteryCellDeviationV', label: 'Dev', icon: 'fa-arrow-down-up-across-line', width: 62, get: (r) => num(r.batteryCellDeviationV, 3) },
      { key: 'battery2VoltageV', label: 'Volts 2', icon: 'fa-bolt', width: 72, get: (r) => num(r.battery2VoltageV, 2) },
      { key: 'battery2CurrentA', label: 'Amps 2', icon: 'fa-gauge', width: 72, get: (r) => num(r.battery2CurrentA, 1) },
      { key: 'pitchDeg', label: 'Pitch', icon: 'fa-plane-up', width: 62, get: (r) => num(r.pitchDeg, 0) },
      { key: 'rollDeg', label: 'Roll', icon: 'fa-plane-up', width: 60, get: (r) => num(r.rollDeg, 0) },
      { key: 'yawDeg', label: 'Yaw', icon: 'fa-compass', width: 60, get: (r) => num(r.yawDeg, 0) },
    ];

    const columns = all.filter((c) => c.key === 'timeS' || f.has(c.key));

    // The message column is built from the event list rather than a data
    // column, so it is always present. Third, after time and mode, so a warning
    // reads without scrolling across the width of the table, and the widest.
    columns.splice(2, 0, {
      key: '_message', label: 'Message', icon: 'fa-triangle-exclamation',
      align: 'left', className: 'msg', width: 300, get: null,
    });
    return columns;
  }

  render() {
    const cols = this.columns
      .map((c) => `<col style="width:${c.width}px">`)
      .join('');

    // The icon is a cue and the word is the meaning, so both are shown: a
    // column headed by a symbol alone is a guess every time.
    const head = this.columns
      .map((c) => `<th class="${c.align === 'left' ? 'left' : ''}">`
        + (c.icon ? `<i class="fa-solid ${c.icon}" aria-hidden="true"></i>` : '')
        + `${c.label}</th>`)
      .join('');

    // A minimum width equal to the declared total, so a narrow window scrolls
    // horizontally rather than crushing every column to fit.
    const total = this.columns.reduce((sum, c) => sum + c.width, 0);

    this.container.innerHTML = `
      <table class="lv-table" style="min-width:${total}px">
        <colgroup>${cols}</colgroup>
        <thead><tr>${head}</tr></thead>
        <tbody></tbody>
      </table>
    `;

    this.body = this.container.querySelector('tbody');
    this.draw();
  }

  bind() {
    this.container.addEventListener('scroll', () => {
      // The table scrolls itself when following the playhead; that must not be
      // mistaken for the user taking manual control.
      //
      // Time-windowed rather than a boolean flag: setting scrollTop to a value
      // it already holds fires no scroll event, which would leave a flag stuck
      // true and swallow the user's next real scroll.
      const sinceAuto = performance.now() - this._lastAutoScroll;
      if (sinceAuto > 120) this._following = false;
      this.scheduleDraw();
    });

    this.body.addEventListener('click', (event) => {
      const row = event.target.closest('tr[data-index]');
      if (!row) return;
      const index = Number(row.dataset.index);
      this._following = true;
      if (this.onSelect) this.onSelect(index);
    });
  }

  scheduleDraw() {
    if (this._pending) return;
    this._pending = true;
    requestAnimationFrame(() => {
      this._pending = false;
      this.draw();
    });
  }

  draw() {
    const count = this.flight.count;
    const scrollTop = this.container.scrollTop;
    const viewport = this.container.clientHeight || 210;

    // The rendered window moves in blocks, not with every pixel of scroll.
    //
    // Tracking scrollTop directly means the window index changes every time
    // the scroll passes a row boundary. Under smooth playback that is roughly
    // every two frames, so the entire body is rebuilt around two dozen times
    // a second and the table hitches on each one. Snapping the window to a
    // block, with enough overscan to cover a whole block of movement either
    // side, drops that to a rebuild every BLOCK rows.
    const visible = Math.ceil(viewport / ROW_HEIGHT);
    const topRow = Math.floor(scrollTop / ROW_HEIGHT);

    // Math.max(1, ...) here would force a minimum of one block, so the top of
    // the table would start at row BLOCK and rows 0 to 23 would never render.
    const block = Math.floor(topRow / BLOCK);

    const first = Math.max(0, block * BLOCK - OVERSCAN);
    const last = Math.min(count, first + visible + BLOCK + OVERSCAN * 2);

    // Rebuilding the body is expensive: at sixty frames a second it re-parses
    // several hundred cells and the browser repaints the whole table, which is
    // what made playback flicker. If the visible window has not moved there is
    // nothing to rebuild, and moving the highlight is a two-class-swap.
    if (first === this._renderedFirst && last === this._renderedLast) {
      this.paintCurrent();
      return;
    }

    const before = first * ROW_HEIGHT;
    const after = Math.max(0, (count - last) * ROW_HEIGHT);
    const span = this.columns.length;

    let html = before > 0 ? `<tr style="height:${before}px"><td colspan="${span}"></td></tr>` : '';

    for (let i = first; i < last; i++) {
      html += this.rowHtml(i);
    }

    if (after > 0) html += `<tr style="height:${after}px"><td colspan="${span}"></td></tr>`;

    this.body.innerHTML = html;
    this._renderedFirst = first;
    this._renderedLast = last;
    this._currentRow = null;
    this.paintCurrent();
  }

  /** Move the highlight without touching anything else. */
  paintCurrent() {
    if (this._currentRow) {
      this._currentRow.classList.remove('current');
      this._currentRow = null;
    }

    const row = this.body.querySelector(`tr[data-index="${this.current}"]`);

    if (row) {
      row.classList.add('current');
      this._currentRow = row;
    }
  }

  rowHtml(i) {
    const row = this.flight.row(i);
    const events = this.flight.eventsAt(i);

    let severity = '';
    if (events.some((e) => e.severity === 'critical')) severity = 'has-critical';
    else if (events.some((e) => e.severity === 'warning')) severity = 'has-warning';

    const message = events.length > 0 ? escapeHtml(events.map((e) => e.text).join(' · ')) : '';

    const cells = this.columns
      .map((c) => {
        const value = c.key === '_message' ? message : c.get(row);
        const classes = [c.align === 'left' ? 'left' : '', c.className || ''].filter(Boolean).join(' ');
        return `<td class="${classes}">${value}</td>`;
      })
      .join('');

    return `<tr data-index="${i}" class="${severity}">${cells}</tr>`;
  }

  /**
   * Follow the playhead.
   *
   * Scrolls only when the current row nears an edge, rather than recentring on
   * every frame. Continuous recentring means the whole table slides under the
   * cursor at playback speed, which reads as juddering even though it is
   * technically smooth.
   */
  setCurrent(index, fraction = undefined) {
    const viewport = this.container.clientHeight || 210;

    if (this._following) {
      this._lastAutoScroll = performance.now();

      if (fraction === undefined) {
        // Scrubbing: nudge only when the row nears an edge. Following a
        // dragged cursor continuously means the content slides under the
        // pointer, which fights the drag.
        const rowTop = index * ROW_HEIGHT;
        const scrollTop = this.container.scrollTop;
        const margin = ROW_HEIGHT * 3;

        if (rowTop < scrollTop + margin
            || rowTop > scrollTop + viewport - margin - ROW_HEIGHT) {
          this.container.scrollTop = Math.max(0, rowTop - viewport / 2);
        }
      } else {
        // Playing: track the playhead exactly, at the fractional row.
        //
        // Waiting for the row to reach an edge and then jumping half a
        // viewport is what made playback stutter: the table sat still for ten
        // rows and then moved a hundred pixels at once. Constant velocity has
        // no discontinuity to notice, and the fraction is what allows it,
        // since the row index alone only changes ten times a second.
        this.container.scrollTop = Math.max(0, fraction * ROW_HEIGHT - viewport / 2);
      }
    }

    if (index === this.current) return;

    this.current = index;
    this.scheduleDraw();
  }

  /**
   * Resume following the playhead.
   *
   * Called when playback starts. The table stops following as soon as it is
   * scrolled by hand, which is right while that is happening and wrong the
   * next time someone presses play.
   */
  follow() {
    this._following = true;
    this._lastAutoScroll = performance.now();

    // Jump to the current row rather than waiting for the next frame to drift
    // there: playback may resume a long way from wherever the table was left,
    // and scrolling smoothly across ten thousand rows is not smooth, it is
    // absent.
    const viewport = this.container.clientHeight || 210;
    this.container.scrollTop = Math.max(0, this.current * ROW_HEIGHT - viewport / 2);

    this.scheduleDraw();
  }

  /**
   * Scroll a row to the top of the viewport and select it.
   *
   * Distinct from the follow behaviour, which keeps the playhead comfortably
   * in view. Jumping to a warning is a request to read forward from it, so it
   * belongs at the top, not centred or clinging to the bottom edge.
   */
  revealAtTop(index) {
    this.current = index;
    this._following = true;
    this._lastAutoScroll = performance.now();
    this.container.scrollTop = Math.max(0, index * ROW_HEIGHT);
    this.scheduleDraw();
  }

  /** Rebuild headers and cells after a unit-system change. */
  refreshUnits() {
    this.columns = this.buildColumns();
    this._renderedFirst = -1;
    this._renderedLast = -1;
    this.render();
    this.setCurrent(this.current);
  }

  destroy() {
    this.container.innerHTML = '';
  }
}

function num(value, decimals) {
  if (value === null || value === undefined) return '—';
  return value.toFixed(decimals);
}

/**
 * Convert an SI value for display. Returns the bare number: the unit lives in
 * the column header, so repeating it on 18,000 rows is noise.
 */
function conv(value, kind, decimals) {
  if (value === null || value === undefined) return '—';
  const formatted = units[kind](value, decimals);
  return formatted.split(' ')[0];
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
